import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { TSessionState } from "./sessionStore.types";

const SESSION_STORE_KEY = "session-store";

const adaptiveStorage = {
  getItem: (name: string): string | null => {
    return sessionStorage.getItem(name) ?? localStorage.getItem(name);
  },
  setItem: (name: string, value: string): void => {
    try {
      const rememberMe = (JSON.parse(value) as { state?: { rememberMe?: boolean } })?.state
        ?.rememberMe;
      if (rememberMe) {
        localStorage.setItem(name, value);
        sessionStorage.removeItem(name);
      } else {
        sessionStorage.setItem(name, value);
        localStorage.removeItem(name);
      }
    } catch {
      sessionStorage.setItem(name, value);
    }
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
  },
};

export { SESSION_STORE_KEY };

const initialSessionState = {
  sessionId: null,
  userId: null,
  tenantId: null,
  email: null,
  firstName: null,
  lastName: null,
  userType: null,
  isEntityLogin: false,
  isMainAppLogin: false,
  subDomain: null,
  modules: [],
  userName: null,
  userRoles: [],
  entityName: null,
  rememberMe: false,
  isReactiveHire: false,
  isAppLevel: false,
  allEntities: false,
  entityIds: [],
};

export const useSessionStore = create<TSessionState>()(
  persist(
    (set) => ({
      ...initialSessionState,
      setSessionId: (value) => set({ sessionId: value }),
      setUserId: (value) => set({ userId: value }),
      setTenantId: (value) => set({ tenantId: value }),
      setEmail: (value) => set({ email: value }),
      setFirstName: (value) => set({ firstName: value }),
      setLastName: (value) => set({ lastName: value }),
      setUserType: (value) => set({ userType: value }),
      setIsEntityLogin: (value) => set({ isEntityLogin: value }),
      setIsMainAppLogin: (value) => set({ isMainAppLogin: value }),
      setSubDomain: (value) => set({ subDomain: value }),
      setModules: (value) => set({ modules: value }),
      setUserName: (value) => set({ userName: value }),
      setUserRoles: (value) => set({ userRoles: value }),
      setEntityName: (value) => set({ entityName: value }),
      setRememberMe: (value) => set({ rememberMe: value }),
      setIsReactiveHire: (value) => set({ isReactiveHire: value }),
      setIsAppLevel: (value) => set({ isAppLevel: value }),
      setEntityIds: (value) => set({ entityIds: value }),
      setSessionData: (value) => set(value),
      resetSessionStore: () => set(initialSessionState),
    }),
    {
      name: SESSION_STORE_KEY,
      storage: createJSONStorage(() => adaptiveStorage),
    },
  ),
);
